import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { showMessage } from 'app/store/fuse/messageSlice';
import axios from 'axios';
import { omit } from 'lodash';
import { markedShippedMessage } from 'app/main/apps/shipmentLoad/shipmentLoadRules';

export const getCheckList = createAsyncThunk(
    'backofficeApp/shipment/getCheckList',
    async (id) => {
        const response = await axios.get(`api/getPackingCheckLists/${id}`);
        const data = await response.data.shipment;
        // PGSS-5478: initialState and the rejected reducer both hold [], but fulfilled assigned this
        // straight through -- so a response without `shipment` (a timeout, a dropped connection, an
        // error body) replaced the safe [] with undefined and every checkList[0] read downstream
        // threw during render, blanking the whole Details screen.
        return data ?? [];
    }
);

export const getShipmentDetails = createAsyncThunk(
    'backofficeApp/shipment/details', async (inputData, { dispatch }) => {
        dispatch(setDetailsLoading(true));
        // Added condition to avoid backend error
        if (inputData === null || inputData === undefined) {
            dispatch(setDetailsLoading(false));
            return {shipment : []};
        }
        const response = await axios.get(`/api/shipmentSummary/${inputData}`)
            .finally(() => dispatch(setDetailsLoading(false)))
        return response.data;
    }
)

export const updateShipment = createAsyncThunk(
    'backofficeApp/shipment/updateShipment',
    // PGSS-5750 (review of #2746): returns the request, and rejects with the server's status, so the caller knows
    // whether the shipment was marked shipped and can ask for the confirmation on a "still to load" refusal.
    async ({ values, shipmentId }, { dispatch, rejectWithValue }) => {
        const payload = {
            ...values,
            is_selected: true,
            images: Array.isArray(values?.new_documents) ? values?.new_documents?.map((item) => ({ id: item.id })) : undefined
        }
        payload.shipping_cost = Number(payload.shipping_cost)
        return axios.put(`api/packingCheckList/${shipmentId}`, omit(payload, ['attachmentId', 'attachmentNewId', 'new_documents']))
        .then((response) => {
            dispatch(showMessage({
                // PGSS-5801: names the orders the items taken off went to.
                message: markedShippedMessage(response?.data),
                variant: 'success',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            }));
        })
        // PGSS-5750: say why, e.g. boxes still to load on a shipment someone scanned.
        .catch((error) => {
            dispatch(showMessage({
                message: error?.response?.data?.message || 'Failed to update',
                variant: 'error',
                autoHideDuration: null,
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            }));
            return rejectWithValue({ status: error?.response?.status, message: error?.response?.data?.message });
        })
    }
);

export const getPackingSlip = createAsyncThunk(
    'backofficeApp/shipment/packingSlip',
    async ({ shipmentId, orderId }) => {
        const response = await axios.get(`api/packingSlip/${shipmentId}`, { params: { oid: orderId } })
        return response.data.shipment
    }
);

export const getShipmentInformation = createAsyncThunk(
    'backofficeApp/shipment/getShipmentInformation',
    async (id) => {
        const response = await axios.get(`api/shipmentInfo/${id}`);
        const data = await response.data.shipment;
        return data;
    }
);

export const getProductionSheet = createAsyncThunk(
    'backofficeApp/shipment/getProductionSheet',
    async (id) => {
        const response = await axios.get(`api/productionSheet/${id}`);
        const data = await response.data.shipping_label;
        return data;
    }
);

export const sendPackingSlipToCustomer = createAsyncThunk(
    'backofficeApp/shipment/sendPackingSlipToCustomer',
    async (id) => {
        const response = await axios.get(`api/send/productionSheet/${id}`);
        const data = await response.data;
        return data;
    }
);

export const viewShipmentForImageUpload = createAsyncThunk(
    'backofficeApp/shipment/viewShipmentForImageUpload',
    async (id) => {
        const response = await axios.get(`api/viewShipment/${id}`);
        const data = await response.data;
        return data;
    }
);

export const updateShipmentForImageUpload = createAsyncThunk(
    'backofficeApp/shipment/vupdateShipmentForImageUpload',
    async (inputData) => {
        const response = await axios.put(`/api/updateShipment/${inputData.id}`, inputData);
        const data = await response.data.data;
    }
);

export const initState = {
    checkList: [],
    updateLoading: false,
    checkListLoading: false,
    enableSave: false,
    fileIds: [],
    packingSlip: [],
    shipmentDialog: {
        open: false
    },
    detailsLoading: false,
    shipentDetails: []
};

const shipmentSlice = createSlice({
    name: 'backofficeApp/shipment',
    initialState: initState,
    reducers: {
        enableSave: (state, action) => {
            state.enableSave = action.payload;
        },
        setFileIds: (state, action) => {
            state.fileIds = action.payload;
        },
        openShipmentDialog: (state, action) => {
            state.shipmentDialog.open = true
        },
        closeShipmentDialog: (state, action) => {
            state.shipmentDialog.open = false
        },
        setDetailsLoading: (state, action) => {
            state.detailsLoading = action.payload
        }
    },
    extraReducers: {
        [getCheckList.pending]: (state, action) => {
            state.checkListLoading = true
        },
        [getCheckList.rejected]: (state, action) => {
            state.checkListLoading = false
            state.checkList = []
        },
        [getCheckList.fulfilled]: (state, action) => {
            state.checkList = action.payload
            state.checkListLoading = false
        },
        [updateShipment.fulfilled]: (state) => {
            state.updateLoading = false
        },
        [updateShipment.pending]: (state) => {
            state.updateLoading = true
        },
        [updateShipment.fulfilled]: (state) => {
            state.updateLoading = false
        },
        [getPackingSlip.fulfilled]: (state, action) => {
            state.packingSlip = action.payload
        },
        [getShipmentDetails.fulfilled]: (state, { payload }) => {
            state.shipentDetails = payload.shipment;
        },
        [getShipmentDetails.rejected]: (state) => {
            state.shipentDetails = []
        },
    },
});

export const {
    enableSave,
    setFileIds,
    openShipmentDialog,
    closeShipmentDialog,
    setDetailsLoading
} = shipmentSlice.actions;

export const selectShipment = ({ backofficeApp }) => backofficeApp.shipment;

export default shipmentSlice.reducer;
